import { getMarketHasAddress } from '~/constants/markets';
import { ClientType, CountryCode } from '~/enums';
import {
  formatAmountWithoutCurrency,
  formatOrganizationNumber,
  removeNonDigits,
} from '~/helpers/formatters.helper';
import { normalizeOrgNumberHelper } from '~/helpers/normalizeOrgNumber.helper';
import { transformGuarantorAddress } from '~/helpers/shared.helper';
import { getCookieValue } from '~/helpers/utils';
import {
  DataPointsShared,
  ILoanApplicationForm,
  ILoanApplicationGuarantor,
} from '~/interfaces/loanApplication';
import { TLoanApplicationDTO } from '~/types/DTO/loanApplication';

const applicationFromDTO = (
  data: TLoanApplicationDTO['getApplicationRes'],
  currentForm: ILoanApplicationForm
): ILoanApplicationForm => {
  return {
    ...currentForm,
    applicant: {
      ...currentForm.applicant,
      ...data.applicant,
    },
    applicationUuid: data.applicationUuid,
    bankIntegration: data.bankIntegration,
    bankConnected: data.bankConnected,
    organizationNumber: formatOrganizationNumber(
      data.organization.organizationNumber || ''
    ),
    connectId: data.organization.connectId,
    vatNumber: data.organization.vatNumber,
    companyName: data.organization.companyName ?? '',
    companyAddress: data.organization.companyAddress,
    companyType: data.organization.companyType || '',
    companyTypeCategory: data.companyTypeCategory,
    purposeOfLoan: currentForm.purposeOfLoan || data.purposeOfLoan,
    reason: (currentForm.reason || data.reason) ?? '',
    sessionId: data.sessionId,
    guarantors:
      data.guarantors?.map(
        (g): ILoanApplicationGuarantor => ({
          ...g,
          guarantorId: g.guarantorId,
          isChecked: true,
        })
      ) || [],
    turnover: currentForm.turnover || data.turnover,
    incomeReceivedInForeignCurrencies:
      currentForm.incomeReceivedInForeignCurrencies ??
      data.incomeReceivedInForeignCurrencies,
    amount:
      currentForm.amount ||
      formatAmountWithoutCurrency(removeNonDigits(data.amount.toString())),
    clientType:
      currentForm.clientType !== ClientType.Unknown
        ? currentForm.clientType
        : data.clientType || ClientType.Unknown,
    applicationId: data.applicationId,
    ...(!!data.bankFilesPaths?.length && {
      bankFilesPaths: data.bankFilesPaths,
    }),
    ...(!!data.bankFilesUploaded && {
      bankFilesUploaded: data.bankFilesUploaded,
    }),
    qCode: data.qCode,
    userCode: data.userCode,
    eskatConnected: data.eskatConnected,
    choseToBoostApplication: data.choseToBoostApplication,
    choseToSubmitBasicApplication: data.choseToSubmitBasicApplication,
  };
};

const applicationToDTO = ({
  form,
  currentStepNumber,
  market,
  shouldPostApplication,
  finalized,
  currentStepCompleted,
  clientId,
  isCompanyTypeCategoryRequested,
  dataPointsShared,
}: {
  clientId?: number;
  currentStepNumber: number;
  currentStepCompleted: boolean;
  finalized: boolean;
  form: ILoanApplicationForm;
  market: CountryCode;
  shouldPostApplication: boolean;
  isCompanyTypeCategoryRequested?: boolean;
  dataPointsShared: DataPointsShared;
}): TLoanApplicationDTO['postPatchApplicationReq'] => {
  const {
    organizationNumber,
    companyType,
    companyName,
    vatNumber,
    connectId,
    companyAddress,
    guarantors,
    amount = '',
    applicant,
    ...rest
  } = form;

  const marketHasAddress = getMarketHasAddress(market);

  const requestData: TLoanApplicationDTO['postPatchApplicationReq'] = {
    organization: {},
    amount: Number(removeNonDigits(amount)),
    completedStep: currentStepCompleted
      ? currentStepNumber
      : currentStepNumber - 1,
    market,
    currentStep: currentStepNumber,
    finalized,
    isCompanyTypeCategoryRequested,
    clientId,
    dataPointsShared,
    applicant,
    ...rest,
  };

  organizationNumber &&
    (requestData.organization.organizationNumber = normalizeOrgNumberHelper(
      organizationNumber,
      market
    ));
  companyType && (requestData.organization.companyType = companyType);
  companyName && (requestData.organization.companyName = companyName);
  vatNumber && (requestData.organization.vatNumber = vatNumber);
  connectId && (requestData.organization.connectId = connectId);
  companyAddress && (requestData.organization.companyAddress = companyAddress);

  if (shouldPostApplication) {
    const trafficCookie = getCookieValue('_ga') || '';
    requestData.trafficCookie = trafficCookie;
  }

  if (guarantors && guarantors.length) {
    requestData.guarantors = guarantors
      .filter((guarantor) => guarantor.isChecked)
      .map(
        (
          formGuarantor
        ): TLoanApplicationDTO['postPatchApplicationReqGuarantor'] => ({
          guarantorId: formGuarantor.guarantorId,
          fullName: formGuarantor.fullName || '',
          email: formGuarantor.email || '',
          phone: formGuarantor.phone || '',
          personalNumber: formGuarantor.personalNumber,
          maskedPersonalNumber: formGuarantor.maskedPersonalNumber,
          dateOfBirth: formGuarantor.dateOfBirth,
          maskedDateOfBirth: formGuarantor.maskedDateOfBirth,
          addressFields: formGuarantor.addressFields,
        })
      );
  }

  if (marketHasAddress) {
    if (requestData.guarantors?.length) {
      requestData.guarantors = requestData.guarantors.map(
        (
          guarantor
        ): TLoanApplicationDTO['postPatchApplicationReqGuarantor'] => {
          if (guarantor.addressFields) {
            return {
              ...guarantor,
              fullAddress: transformGuarantorAddress({
                ...guarantor.addressFields,
                market,
              }),
            };
          }
          return guarantor;
        }
      );
    }
  }

  return requestData;
};

export const loanApplicationDTOMappers = {
  applicationFromDTO,
  applicationToDTO,
};
